import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Pencil, X } from "lucide-react";
import { CALENDAR_LIBRARY_CATEGORIES, SYSTEM_SCHEDULE_ROOT_TEMPLATES, calendarLibraryCategory, getScheduleActivityIcon, getSystemScheduleTemplateIcon } from "../lib/trainingPlanUnits";
import { RUNNING_TRAINING_CHOICES, runningChoiceLabelKey } from "../lib/runningTrainingSelection";
import { effectiveTrainingTemplates, runningSystemId, trainingGroupId } from "../lib/trainingLibrarySelection";
import { trainingLibraryText } from "../lib/trainingLibrary";
import "./CalendarTrainingChooser.css";

const PAGE_SIZE = 8;
const COPY = {
  de: { close: "Schließen", cancel: "Abbrechen", search: "Trainings suchen", empty: "Keine passenden Einheiten gefunden.", hint: "Einheit auswählen oder auf einen Kalendertag ziehen.", previous: "Vorherige Seite", next: "Nächste Seite", page: "Seite", of: "von", results: "Einheiten", global: "Globale Vorlage", inactive: "Deaktiviert", edit: "Bearbeiten", deactivate: "Deaktivieren" },
  en: { close: "Close", cancel: "Cancel", search: "Search workouts", empty: "No matching sessions found.", hint: "Choose a session or drag it onto a calendar day.", previous: "Previous page", next: "Next page", page: "Page", of: "of", results: "sessions", global: "Global template", inactive: "Deactivated", edit: "Edit", deactivate: "Deactivate" },
};

export const CALENDAR_RUNNING_TEMPLATES = RUNNING_TRAINING_CHOICES.map(choice => ({
  id: runningSystemId(choice), category: "training", labelKey: runningChoiceLabelKey(choice),
  ...(choice === "tennis_interval" || choice === "sidestep"
    ? { unitType: "individual_training", training_template: choice }
    : { unitType: "running_training", running_variant: choice }),
}));

// Resolve publication/version identity before grouping: a moved system revision must
// not leave an extra fallback in its previous group. Independent workouts stay distinct.
export function calendarTrainingOptions(library = [], groupId = "training-running", category = "training") {
  if (!CALENDAR_LIBRARY_CATEGORIES.includes(category)) return [];
  const effective = effectiveTrainingTemplates(library.filter(row => row.scope !== "global" || row.status === "published"));
  const belongs = row => calendarLibraryCategory(row) === category && (category !== "training" || trainingGroupId(row) === groupId);
  const systems = [...SYSTEM_SCHEDULE_ROOT_TEMPLATES.filter(row => row.id !== "system-running-training"), ...CALENDAR_RUNNING_TEMPLATES].filter(belongs);
  const identity = row => row.system_template_id || row.id;
  const records = effective.filter(belongs);
  return [
    ...systems.flatMap(template => {
      const record = effective.find(row => identity(row) === template.id);
      if (record && !belongs(record)) return [];
      return [{ template: record || template, source: record ? "planning-template" : "system-template", choice: RUNNING_TRAINING_CHOICES.find(choice => runningSystemId(choice) === template.id) }];
    }),
    ...records.filter(row => !systems.some(template => template.id === identity(row))).map(template => ({ template, source: "planning-template" })),
  ];
}

export default function CalendarTrainingChooser({ title, date, items = [], selectedChoice, lang = "de", t, onChoose, onCancel, onDragState, onEdit, onDeactivate, saving }) {
  const copy = COPY[lang] || COPY.de;
  const opener = useRef(document.activeElement);
  const chosen = useRef(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [dragging, setDragging] = useState(false);
  const label = option => option.source === "system-template" ? t(option.template.labelKey) : trainingLibraryText(option.template, "title", lang);
  const normalizedQuery = query.trim().toLocaleLowerCase(lang);
  const filtered = items.filter(option => [label(option), trainingLibraryText(option.template, "short_description", lang)].join(" ").toLocaleLowerCase(lang).includes(normalizedQuery));
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages - 1);
  const visible = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const leaveForEditor = action => { chosen.current = true; action(); };

  useEffect(() => {
    if (!dragging) return undefined;
    // Keep the native drag source mounted. CSS exposes only calendar drop targets
    // while Radix retains ownership of its body pointer/scroll locking lifecycle.
    const drop = event => {
      if (!event.target.closest?.("[data-calendar-drop-date]")) return;
      chosen.current = true;
      onCancel();
    };
    const end = () => { setDragging(false); onDragState?.(null); };
    document.addEventListener("drop", drop, true);
    document.addEventListener("dragend", end);
    return () => {
      document.removeEventListener("drop", drop, true);
      document.removeEventListener("dragend", end);
    };
  }, [dragging, onCancel, onDragState]);

  return <Dialog.Root open onOpenChange={open => { if (!open) { onDragState?.(null); onCancel(); } }}>
    <Dialog.Portal>
      <Dialog.Overlay className="calendar-training-chooser-overlay" data-dragging={dragging || undefined} />
      <Dialog.Content aria-modal="true" data-testid="calendar-training-chooser" className="calendar-training-chooser" data-dragging={dragging || undefined}
        onCloseAutoFocus={event => { event.preventDefault(); if (!chosen.current && opener.current?.isConnected) opener.current.focus(); }}>
        <header className="calendar-training-chooser-header">
          <div><Dialog.Title className="font-display font-semibold">{title}</Dialog.Title>
            <Dialog.Description className="calendar-training-chooser-hint">{copy.hint}{date && <><br /><time dateTime={date}>{date}</time></>}</Dialog.Description></div>
          <Dialog.Close asChild><button type="button" className="calendar-training-chooser-close" aria-label={copy.close}><X size={20} aria-hidden="true" /></button></Dialog.Close>
        </header>
        {items.length > PAGE_SIZE && <label className="calendar-training-chooser-search">{copy.search}<input type="search" className="pc-input" value={query} onChange={event => { setQuery(event.target.value); setPage(0); }} /></label>}
        <div className="calendar-training-chooser-body">
          <div className="calendar-training-chooser-grid" data-testid="calendar-training-chooser-grid">
            {visible.map(option => {
              const { template, source, choice } = option;
              const active = template.active !== false;
              const system = source === "system-template";
              const global = template.scope === "global";
              const Icon = system ? getSystemScheduleTemplateIcon(template) : getScheduleActivityIcon({ ...template, category: template.kind });
              const name = label(option);
              const detail = trainingLibraryText(template, "short_description", lang);
              const duration = template.default_duration_minutes || template.duration;
              return <article key={`${source}-${template.id}`} data-testid={`${system ? "system-template" : "library-template"}-${template.id}`} className="calendar-training-chooser-card" data-inactive={!active || undefined}>
                <button type="button" className="calendar-training-chooser-tile" data-testid={choice ? `running-choice-${choice}` : undefined} aria-label={name} aria-pressed={choice && selectedChoice ? selectedChoice === choice : undefined}
                  disabled={!active} draggable={active && !!onDragState} onClick={() => leaveForEditor(() => onChoose(option))}
                  onDragStart={event => {
                    event.dataTransfer.setData(source, template.id);
                    event.dataTransfer.effectAllowed = "copy";
                    onDragState?.({ kind: "template", id: template.id, deletable: false });
                    setDragging(true);
                  }}>
                  <span className="calendar-training-chooser-meta"><Icon size={18} aria-hidden="true" />{global && <span title={copy.global}>v{template.version}</span>}{!active && <span>{copy.inactive}</span>}</span>
                  <span className="calendar-training-chooser-name">{name}</span>
                  {(detail || duration) && <span className="calendar-training-chooser-detail" title={detail || undefined}>{detail || `${duration} min`}</span>}
                </button>
                {!system && !global && (onEdit || onDeactivate) && <div className="calendar-training-chooser-actions">
                  {onEdit && <button type="button" onClick={() => leaveForEditor(() => onEdit(template))}><Pencil size={12} aria-hidden="true" />{copy.edit}</button>}
                  {active && onDeactivate && <button type="button" disabled={saving} onClick={() => onDeactivate(template)}>{copy.deactivate}</button>}
                </div>}
              </article>;
            })}
          </div>
          {!filtered.length && <p role="status" className="calendar-training-chooser-empty">{copy.empty}</p>}
        </div>
        <footer className="calendar-training-chooser-footer">
          {items.length > PAGE_SIZE && <nav className="calendar-training-chooser-pagination" aria-label={copy.page}>
            <button type="button" aria-label={copy.previous} disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}><ChevronLeft size={18} aria-hidden="true" /></button>
            <span role="status">{copy.page} {currentPage + 1} {copy.of} {pages}<small>{filtered.length} {copy.results}</small></span>
            <button type="button" aria-label={copy.next} disabled={currentPage === pages - 1} onClick={() => setPage(currentPage + 1)}><ChevronRight size={18} aria-hidden="true" /></button>
          </nav>}
          <Dialog.Close asChild><button type="button" className="pc-btn pc-btn-ghost">{copy.cancel}</button></Dialog.Close>
        </footer>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
